import { z } from 'zod';
import type { WidgetSession } from './widget-session';

export const BUBBLE_REQUEST_EVENT = 'move-helpdesk:bubble-identity-request';
export const BUBBLE_RESULT_EVENT = 'move-helpdesk:bubble-identity-result';
export const BUBBLE_CHANGED_EVENT = 'move-helpdesk:bubble-identity-changed';

const result = z.discriminatedUnion('status', [
  z
    .object({
      requestId: z.uuid(),
      status: z.literal('connected'),
      credential: z.string().min(1).max(8192),
      expiresAt: z.iso.datetime(),
    })
    .strict(),
  z.object({ requestId: z.uuid(), status: z.enum(['guest', 'unmapped', 'unavailable']) }).strict(),
]);

export interface BubbleSnapshot {
  status: 'resolving' | 'guest' | 'connected' | 'reconnect';
  generation: number;
  epoch: number;
}

const EXPIRY_SKEW_MS = 15_000;
const REQUEST_TIMEOUT_MS = 15_000;

function tokenSubject(credential: string): string | null {
  try {
    const payload = credential.split('.')[1];
    if (!payload) return null;
    const parsed = z
      .object({ sub: z.string().min(1) })
      .safeParse(JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as unknown);
    return parsed.success ? parsed.data.sub : null;
  } catch {
    return null;
  }
}

/** Memory-only bridge for a Bubble workflow that proves Current User on Bubble's server. */
export class BubbleSession {
  private value: BubbleSnapshot = { status: 'resolving', generation: 0, epoch: 0 };
  private listeners = new Set<() => void>();
  private access: { credential: string; expiresAt: string } | null = null;
  private actorPartition: string | null = null;
  private accountIntent = false;
  private hostActorChanged = false;
  private active = false;
  private moveHadActor = false;
  private disposed = false;
  private pending: { requestId: string; resolve: () => void; timer: number } | null = null;
  private pendingPromise: Promise<void> | null = null;
  private unsubscribeMove: () => void;

  constructor(private readonly move: WidgetSession) {
    window.addEventListener(BUBBLE_RESULT_EVENT, this.onResult);
    window.addEventListener(BUBBLE_CHANGED_EVENT, this.onChanged);
    this.unsubscribeMove = move.subscribe(() => {
      if (move.isSuppressed()) this.clear();
      else if (move.hasAccountActor() && !this.moveHadActor) this.clear();
      this.moveHadActor = move.hasAccountActor();
    });
    this.moveHadActor = move.hasAccountActor();
    if (move.isSuppressed()) this.clear();
  }

  snapshot = (): BubbleSnapshot => this.value;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  hasAccountActor = (): boolean => this.accountIntent;

  activate(): void {
    this.active = true;
    if (this.move.isSuppressed() || this.move.hasAccountActor() || this.accountIntent) return;
    this.update('resolving');
    void this.request();
  }

  deactivate(): void {
    this.active = false;
    this.cancelPending();
  }

  private update(status: BubbleSnapshot['status'], actorChanged = false): void {
    this.value = {
      ...this.value,
      status,
      generation: this.value.generation + (actorChanged ? 1 : 0),
    };
    for (const listener of this.listeners) listener();
  }

  private cancelPending(): void {
    if (!this.pending) return;
    window.clearTimeout(this.pending.timer);
    this.pending.resolve();
    this.pending = null;
    this.pendingPromise = null;
  }

  private clear(): void {
    const actorChanged = this.accountIntent || this.value.status !== 'guest';
    this.value = { ...this.value, epoch: this.value.epoch + 1 };
    this.cancelPending();
    this.access = null;
    this.actorPartition = null;
    this.accountIntent = false;
    this.hostActorChanged = false;
    this.update('guest', actorChanged);
  }

  private onChanged = (): void => {
    if (this.disposed || this.move.isSuppressed() || this.move.hasAccountActor()) return;
    this.value = { ...this.value, epoch: this.value.epoch + 1 };
    this.cancelPending();
    this.access = null;
    this.hostActorChanged = true;
    this.update(this.accountIntent ? 'reconnect' : 'resolving', true);
    if (this.active && !this.move.hasAccountActor()) void this.request();
  };

  private onResult = (event: Event): void => {
    if (
      this.disposed ||
      this.move.isSuppressed() ||
      this.move.hasAccountActor() ||
      !(event instanceof CustomEvent)
    )
      return;
    const parsed = result.safeParse(event.detail);
    if (!parsed.success || parsed.data.requestId !== this.pending?.requestId) return;
    const { status } = parsed.data;
    this.cancelPending();
    if (status === 'connected') {
      if (Date.parse(parsed.data.expiresAt) <= Date.now() + EXPIRY_SKEW_MS) {
        this.access = null;
        this.update('reconnect');
        return;
      }
      const partition = tokenSubject(parsed.data.credential);
      if (!partition) {
        this.access = null;
        this.update('reconnect');
        return;
      }
      this.access = { credential: parsed.data.credential, expiresAt: parsed.data.expiresAt };
      const actorChanged =
        !this.accountIntent ||
        !partition ||
        !this.actorPartition ||
        partition !== this.actorPartition;
      if (actorChanged && this.accountIntent)
        this.value = { ...this.value, epoch: this.value.epoch + 1 };
      this.actorPartition = partition;
      this.accountIntent = true;
      this.hostActorChanged = false;
      this.update('connected', actorChanged);
      return;
    }
    if (status === 'unavailable') {
      this.access = null;
      this.update('reconnect');
      return;
    }
    if (status === 'unmapped' && this.accountIntent && !this.hostActorChanged) {
      this.access = null;
      this.update('reconnect');
      return;
    }
    this.clear();
  };

  private request(): Promise<void> {
    if (this.disposed || this.move.isSuppressed() || !this.active || this.move.hasAccountActor())
      return Promise.resolve();
    if (this.pendingPromise) return this.pendingPromise;
    const requestId = crypto.randomUUID();
    const promise = new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        if (this.pending?.requestId !== requestId) return;
        this.cancelPending();
        this.update('reconnect');
      }, REQUEST_TIMEOUT_MS);
      this.pending = { requestId, resolve, timer };
    });
    this.pendingPromise = promise;
    queueMicrotask(() => {
      if (this.pending?.requestId === requestId)
        window.dispatchEvent(new CustomEvent(BUBBLE_REQUEST_EVENT, { detail: { requestId } }));
    });
    return promise;
  }

  async getAccessToken(): Promise<string> {
    if (this.disposed || this.move.isSuppressed() || this.move.hasAccountActor() || !this.active)
      throw new Error('Bubble Helpdesk unavailable.');
    if (this.access && Date.parse(this.access.expiresAt) > Date.now() + EXPIRY_SKEW_MS)
      return this.access.credential;
    const epoch = this.value.epoch;
    const generation = this.value.generation;
    await this.request();
    if (
      epoch !== this.value.epoch ||
      generation !== this.value.generation ||
      !this.access ||
      Date.parse(this.access.expiresAt) <= Date.now() + EXPIRY_SKEW_MS
    )
      throw new Error('Bubble Helpdesk connection unavailable.');
    return this.access.credential;
  }

  retry(): void {
    if (this.disposed || this.move.isSuppressed() || this.move.hasAccountActor() || !this.active)
      return;
    this.update(this.accountIntent ? 'reconnect' : 'resolving');
    void this.request();
  }

  reject(): void {
    this.access = null;
    this.update('reconnect');
  }

  dispose(): void {
    this.disposed = true;
    this.value = { ...this.value, epoch: this.value.epoch + 1 };
    this.access = null;
    this.cancelPending();
    this.unsubscribeMove();
    window.removeEventListener(BUBBLE_RESULT_EVENT, this.onResult);
    window.removeEventListener(BUBBLE_CHANGED_EVENT, this.onChanged);
    this.listeners.clear();
  }
}
